import axios, { AxiosResponse, isAxiosError } from 'axios';
import { ICurrentUserInformation, IUser, Role } from 'types';
import { Router } from 'vue-router';
import { ActionContext, createStore, StoreOptions } from 'vuex';
import endpointsList from './helpers/endpointsList';
import { Route } from './routes';
import { Env, IEnv } from './interfaces/env';

export interface IVueexState {
  adobeClientId: string;
  adobeScriptUrl: string;
  language: string;
  userRoles?: Role[];
  supplierId?: string;
  userData?: IUser;
  currentProjectId?: string;
  currentDigitalizationProjectId?: string;
  isCurrentPatchNotePending: boolean;
  env?: Env;
}

const store: StoreOptions<IVueexState> = {
  state: {
    language: 'de',
    userRoles: [],
    supplierId: undefined,
    userData: undefined,
    isCurrentPatchNotePending: false,
    env: undefined,
    adobeClientId: 'dee16a259a4943d0bbdfe0e0e63867d3',
    adobeScriptUrl: 'https://acrobatservices.adobe.com/view-sdk/viewer.js',
  },
  actions: {
    async fetchCurrentUserInfo(
      context: ActionContext<IVueexState, IVueexState>,
      router: Router,
    ) {
      if (context.state.userData) return;
      try {
        const response: AxiosResponse<ICurrentUserInformation> =
          await axios.get(endpointsList.general.userData);
        const data = response.data;
        context.commit('setCurrentUserInfo', {
          userData: data.userData,
          supplierId: data.supplier?.toString(),
          userRoles: data.roles,
          isCurrentPatchNotePending: data.isCurrentPatchNotePending,
        });
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 403) {
          router.push({ name: Route.FORBIDDEN_VIEW });
        }
        throw err;
      }
    },

    async fetchEnv(context: ActionContext<IVueexState, IVueexState>) {
      const response: AxiosResponse<IEnv> = await axios.get(
        endpointsList.general.env,
      );
      context.commit('setEnv', response.data.environment);
    },

    setAdobeClientId(
      context: ActionContext<IVueexState, IVueexState>,
      environment?: Env,
    ) {
      // dev envs and GS uses *.azurewebsites.net domains
      // *.bew.berlin:        fc98842b530047e7a3b421c1d7427669
      // *.azurewebsites.net: 564f226f36744966864d527829267eaf
      const env = environment || this.state.env;
      if (env === Env.local) {
        this.state.adobeClientId = 'dee16a259a4943d0bbdfe0e0e63867d3';
      } else if (env === Env.dev) {
        this.state.adobeClientId = '564f226f36744966864d527829267eaf';
      } else if (env === Env.prod || env === Env.test) {
        this.state.adobeClientId = 'fc98842b530047e7a3b421c1d7427669';
      } else {
        throw new Error('invalid app source or environment');
      }
    },
  },
  mutations: {
    setLang(state: IVueexState, payload: string) {
      state.language = payload;
    },
    setEnv(state: IVueexState, payload: Env) {
      state.env = payload;
    },

    setCurrentUserInfo(
      state: IVueexState,
      payload: {
        userData: ICurrentUserInformation['userData'];
        supplierId?: string;
        userRoles: ICurrentUserInformation['roles'];
        isCurrentPatchNotePending: boolean;
      },
    ) {
      state.userData = payload.userData;
      state.supplierId = payload.supplierId;
      state.userRoles = payload.userRoles;
      state.isCurrentPatchNotePending = payload.isCurrentPatchNotePending;
    },

    setIsCurrentPatchNotePending(state: IVueexState, isPending: boolean) {
      state.isCurrentPatchNotePending = isPending;
    },
  },
};

export default createStore(store);
